import { ChartLayoutManager, ViewMode } from './ChartLayoutManager'
import { useScreenerHistory } from '@/hooks/useScreenerHistory'
import { rawHistoryDetailLabel, SCREENER_INTERVAL_SECONDS } from '@/lib/screenerHistory'
import { ChartTimeContext } from './ChartTimeNavigation'
import type { ScreenerTickerData, ScreenerTimeframe } from '@/types'
import type { ChartAlertControls } from './ChartLevelAlerts'
import type { ChartLayoutPreferenceControls } from '@/hooks/useChartLayoutPreferences'

interface ChartGridProps {
  tickerData: ScreenerTickerData | null
  symbol: string
  mode: ViewMode
  selectedExchanges: string[]
  priceExchange: string
  interval: string
  orderStorageKey: string
  alertControls?: ChartAlertControls
  timeframe?: ScreenerTimeframe
  onGoToLatest?: () => void
  layoutPreferences?: ChartLayoutPreferenceControls
  includeOIOhlc?: boolean
}

export function ChartGrid(props: ChartGridProps) {
  const context = JSON.stringify([props.symbol, props.interval, props.timeframe, [...props.selectedExchanges].sort(), props.includeOIOhlc ?? false])
  return <HistoryChartGrid key={context} {...props} />
}

function HistoryChartGrid({
  tickerData,
  symbol,
  mode,
  selectedExchanges,
  priceExchange,
  interval,
  orderStorageKey,
  alertControls,
  timeframe,
  onGoToLatest,
  layoutPreferences,
  includeOIOhlc = false,
}: ChartGridProps) {
  const history = useScreenerHistory(symbol, tickerData, interval, timeframe, selectedExchanges, includeOIOhlc)
  const effectiveInterval = history.data?.oi_interval || interval
  const step = SCREENER_INTERVAL_SECONDS[effectiveInterval] || 60
  const narrowRange = history.navigation.range.to - history.navigation.range.from < step * 2
  const showNearby = () => {
    const center = Math.floor((history.navigation.range.from + history.navigation.range.to) / 2 / step) * step
    history.navigation.navigate({ from: center - step, to: center + step * 2 })
  }
  return (
    <ChartTimeContext.Provider value={history.navigation}>
      <div className="mb-2 flex min-h-7 flex-wrap items-center gap-3 px-1 text-xs text-slate-400" aria-live="polite">
        <span>{history.browsing ? 'Browsing history' : 'Live'} · Drag to browse · Scroll to zoom</span>
        {interval === '5s' && <span>{rawHistoryDetailLabel(timeframe, history.navigation.range, history.browsing)}.
          {' '}Showing {new Date(history.navigation.range.from * 1000).toLocaleString()} – {new Date(history.navigation.range.to * 1000).toLocaleString()}.
          {' '}Drag for earlier data; choose a larger interval for the full selected range.</span>}
        {effectiveInterval !== interval && <span>Showing {effectiveInterval} · Older or wider ranges use retained resolution</span>}
        {narrowRange && <button className="underline text-teal-300" onClick={showNearby}>Show nearby {effectiveInterval} samples</button>}
        {history.data?.history_window && Object.values(history.data.exchanges).every(data => !data.oi_history?.length)
          && <span>No stored samples in this window.</span>}
        {history.loading && <span role="status">Loading history…</span>}
        {history.error && <span role="alert" className="text-amber-400">{history.error} <button className="underline" onClick={history.retry}>Retry</button></span>}
        {history.browsing && <button type="button" className="rounded border border-slate-600 px-2 py-1 text-teal-300" onClick={() => { history.goLive(); onGoToLatest?.() }}>Go to latest</button>}
      </div>
      <ChartLayoutManager
        oiLineMode={!includeOIOhlc}
        tickerData={history.data}
        symbol={symbol}
        mode={mode}
        selectedExchanges={selectedExchanges}
        priceExchange={priceExchange}
        interval={effectiveInterval}
        orderStorageKey={orderStorageKey}
        alertControls={alertControls}
        layoutPreferences={layoutPreferences}
      />
    </ChartTimeContext.Provider>
  )
}
